/**
 * The unit cell's range card (AP-764 §3.4, AP-846): an uppercase status
 * heading when out of range, the normal band, an age caveat when the band
 * is a fallback, then the guideline the band comes from under a divider —
 * one line, clickable when the PDF is hosted.
 *
 * It is a Popover rather than a Tooltip because it holds an action. It opens
 * on hover and on focus, and the trigger is a real button so a keyboard user
 * reaches it; Enter or Space on the trigger opens the guideline, since focus
 * does not travel into the floating card. A plain click on the trigger does
 * nothing — opening a PDF from a stray click on the unit would surprise.
 */
import type { KeyboardEvent, ReactNode } from 'react';
import { Popover } from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import type { RangeDirection } from '@/shared/utils/formatNormalRange';
import type { RangeSource } from '@/shared/utils/rangeSource';
import styles from './RangePopover.module.css';

// A sweeping cursor should not flash cards across a dense panel.
const OPEN_DELAY = 0.3;

const HEADING: Record<RangeDirection, string> = {
  high: 'Above normal range',
  low: 'Below normal range',
};

export interface RangePopoverProps {
  range: RangeDirection | 'normal' | 'unmeasured' | null;
  /** Formatted normal band ("4.2 - 5.8", "< 5.9"); null = no band at all. */
  normalRange: string | null;
  unit: string | null;
  /** Caveat under the band (age fallback); only rendered with a band. */
  note?: string | null;
  source?: RangeSource | null;
  /** Opens the hosted guideline PDF; without it the label renders alone. */
  onOpenSource?: (file: string, page?: number | null) => void;
  /** Class for the focusable trigger that wraps the unit cell contents. */
  triggerClassName?: string;
  children: ReactNode;
}

export function RangePopover({
  range,
  normalRange,
  unit,
  note = null,
  source = null,
  onOpenSource,
  triggerClassName,
  children,
}: RangePopoverProps) {
  const heading = range === 'high' || range === 'low' ? HEADING[range] : null;
  const bandText = normalRange
    ? `${normalRange}${unit ? ` ${unit}` : ''}`
    : null;
  const sourceFile = source?.file ?? null;
  const openable = !!(bandText && source && sourceFile && onOpenSource);
  const open = () => {
    if (openable) onOpenSource(sourceFile, source.page);
  };

  const onTriggerKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    open();
  };

  const spoken = [
    bandText ? `Normal range ${bandText}` : 'No reference range',
    heading,
    note,
    source && bandText
      ? `Guideline ${source.label}${openable ? ', press Enter to open' : ''}`
      : null,
  ]
    .filter(Boolean)
    .join('. ');

  const card = (
    <div className={styles.card}>
      {heading && <div className={styles.heading}>{heading}</div>}
      {bandText ? (
        <div className={styles.band}>
          <span className={styles.bandLabel}>Normal</span>
          <span className={styles.bandValue}>{bandText}</span>
        </div>
      ) : (
        <div>No reference range for this measurement</div>
      )}
      {note && bandText && <div className={styles.note}>{note}</div>}
      {source && bandText && (
        <div className={styles.source}>
          {openable ? (
            <button
              type="button"
              className={styles.sourceLink}
              // Keyboard users act on the trigger; the card is pointer-only.
              tabIndex={-1}
              title={`Open guideline: ${source.label}${
                source.location ? `, ${source.location}` : ''
              }`}
              onClick={(e) => {
                e.stopPropagation();
                open();
              }}
            >
              <span className={styles.sourceName}>{source.label}</span>
              <ExportOutlined className={styles.sourceIcon} />
            </button>
          ) : (
            <span className={styles.sourceName}>{source.label}</span>
          )}
        </div>
      )}
    </div>
  );

  return (
    <Popover
      content={card}
      trigger={['hover', 'focus']}
      mouseEnterDelay={OPEN_DELAY}
      color="var(--viewer-popover-bg)"
      classNames={{ container: styles.container }}
    >
      <button
        type="button"
        className={`${styles.trigger} ${triggerClassName ?? ''}`}
        aria-label={spoken}
        onKeyDown={openable ? onTriggerKeyDown : undefined}
      >
        {children}
      </button>
    </Popover>
  );
}
